import { useQueryClient } from "@tanstack/react-query";
import { useSpace } from "../../../hooks/useSpace";
import { DocumentDatabase, DocumentMinimalData } from "../../../types";
import { keys as documentKeys } from "../api/keys";
import { useDeleteDocuments } from "../api/useDeleteDocuments";
import useDeleteFilesFromGoogleDrive from "../api/useDeleteFilesFromGoogleDrive";
import { usePostDocuments } from "../api/usePostDocuments";
import useUploadFilesToGoogleDrive from "../api/useUploadFileToGoogleDrive";

export function useChangeDocumentsOfResource() {
  const { currentSpace } = useSpace();
  const queryClient = useQueryClient();
  const { mutateAsync: postDocuments, isPending: isPostDocumentsPending } = usePostDocuments();
  const { mutateAsync: deleteDocuments, isPending: isDeleteDocumentsPending } = useDeleteDocuments();
  const { mutateAsync: deleteFilesFromDrive } = useDeleteFilesFromGoogleDrive();
  const { mutateAsync: uploadFiles, isPending: isUploadPending } = useUploadFilesToGoogleDrive();

  async function uploadPendingFiles(
    documents: DocumentMinimalData[],
    options: { subjectId?: string; folderName?: string } = {},
  ): Promise<DocumentMinimalData[]> {
    const docsWithFiles = documents.filter(doc => doc.pendingFile);
    const docsWithoutFiles = documents.filter(doc => !doc.pendingFile);

    if (docsWithFiles.length === 0) return documents;

    if (!currentSpace) {
      throw new Error('Aucun espace sélectionné pour téléverser les fichiers.');
    }

    const filesToUpload = docsWithFiles.map(doc => {
      const original = doc.pendingFile!;
      const extMatch = original.name.match(/\.[^.]+$/);
      const ext = extMatch ? extMatch[0] : '';
      const newName = `${doc.name ?? ''}${ext}`;
      if (newName === original.name) return original;
      return new File([original], newName, {
        type: original.type,
        lastModified: original.lastModified,
      });
    });

    const results = await uploadFiles({
      files: filesToUpload,
      spaceId: currentSpace.id,
      subjectId: options.subjectId,
      folderName: options.folderName,
    });

    const uploadedDocs = docsWithFiles.map((doc, index) => ({
      ...doc,
      file_url: results[index].viewUrl,
      pendingFile: undefined,
    }));

    return [...uploadedDocs, ...docsWithoutFiles];
  }

  async function saveDocumentChanges(
    currentDocuments: DocumentDatabase[],
    newDocuments: DocumentMinimalData[],
    resources: {
      event_id?: string;
      sub_topic_id?: string;
      task_id?: string;
      subject_action_id?: string;
      provider_id?: string | null;
      communication_id?: string;
      quote_id?: string;
      incident_id?: string;
    }
  ) {
    const previousIds = new Set(currentDocuments.map(r => r.id));
    const nextIds = new Set(newDocuments.map(r => r.id));

    const added = newDocuments.filter(r => !previousIds.has(r.id));
    const removed = currentDocuments.filter(r => !nextIds.has(r.id));

    if (added.length > 0) {
      if (!currentSpace) {
        throw new Error('Aucun espace sélectionné pour enregistrer les documents.');
      }
      await postDocuments({
        command: added.map(doc => ({
          name: doc.name!,
          file_url: doc.file_url!,
          document_type_id: doc.document_type_id!,
          space_id: currentSpace.id,
          ...resources,
          provider_id: doc.provider_id !== undefined ? doc.provider_id : (resources.provider_id ?? null),
        }))
      });
    }

    if (removed.length > 0) {
      if (!currentSpace) {
        throw new Error('Aucun espace sélectionné pour supprimer les fichiers.');
      }
      await deleteFilesFromDrive({
        fileUrls: removed.map(doc => doc.file_url),
        spaceId: currentSpace.id,
      });
      await deleteDocuments({
        ids: removed.map(doc => doc.id)
      })
    }

    if ((added.length > 0 || removed.length > 0) && resources.sub_topic_id) {
      queryClient.invalidateQueries({ queryKey: documentKeys.getDocumentsBySubTopicId(resources.sub_topic_id) });
    }
  }

  return {
    uploadPendingFiles,
    saveDocumentChanges,
    isPending: isPostDocumentsPending || isDeleteDocumentsPending || isUploadPending
  }
}
